import React, { useEffect, useState, useCallback } from 'react';
import {
    Button, Card, Col, Row, Space, Switch, Tag, Typography, Tooltip,
    Popconfirm, Badge, Statistic, Empty, Alert, Grid, Input, message,
    Avatar
} from 'antd';
import {
    PlusOutlined, ReloadOutlined, EditOutlined, DeleteOutlined,
    EyeOutlined, ShopOutlined, SearchOutlined, EnvironmentOutlined,
    CompassOutlined
} from '@ant-design/icons';
import MainLayout from '../../components/MainLayout';
import { franchiseService } from '../../services/franchiseService';
import { errorMsg, successMsg } from '../../utils/ShowMessage';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { routes } from '../../utils/routes.config';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

// ─── Single Franchise Card ────────────────────────────────────────────────
function FranchiseStatCard({ franchise, onView, onEdit, onDelete, onToggleRadius, togglingRadius }) {
    const screens = useBreakpoint();
    const isSmall = !screens.md;
    const singlePincode = (franchise.pincodes && franchise.pincodes.length > 0)
        ? franchise.pincodes[0]
        : (franchise.pincode || null);

    return (
        <Card
            hoverable
            style={{ borderRadius: 12, border: '1px solid #e2e8f0', height: '100%', display: 'flex', flexDirection: 'column' }}
            bodyStyle={{ padding: isSmall ? 16 : 20, display: 'flex', flexDirection: 'column', flex: 1 }}
        >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                        {franchise.logo ? (
                            <Avatar
                                src={franchise.logo}
                                size={28}
                                shape="square"
                                style={{ borderRadius: 6, flexShrink: 0, border: '1px solid #e2e8f0', background: '#fff' }}
                            />
                        ) : (
                            <ShopOutlined style={{ color: '#2563eb' }} />
                        )}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{franchise.name}</span>
                    </div>
                    {franchise.city && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {franchise.city}{franchise.state ? `, ${franchise.state}` : ''}
                        </Text>
                    )}
                </div>
                <Badge
                    status={franchise.status === 1 ? 'success' : 'default'}
                    text={<span style={{ fontSize: 12 }}>{franchise.status === 1 ? 'Active' : 'Inactive'}</span>}
                />
            </div>

            {/* Address */}
            {franchise.address && (
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <EnvironmentOutlined style={{ color: '#94a3b8' }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{franchise.address}</span>
                </div>
            )}

            {/* Coverage Radius Status & Quick Toggle */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: franchise.radiusEnabled ? '#eff6ff' : '#f8fafc',
                border: `1px solid ${franchise.radiusEnabled ? '#bfdbfe' : '#e2e8f0'}`,
                padding: '6px 10px', borderRadius: 8, marginBottom: 12
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CompassOutlined style={{ color: franchise.radiusEnabled ? '#2563eb' : '#94a3b8', fontSize: 15 }} />
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: franchise.radiusEnabled ? '#1e40af' : '#475569' }}>
                            Coverage Radius: {franchise.deliveryRadiusKm || 10} km
                        </div>
                        <div style={{ fontSize: 10, color: franchise.radiusEnabled ? '#3b82f6' : '#94a3b8' }}>
                            {franchise.radiusEnabled ? 'Distance filtering active' : 'Radius filtering off'}
                        </div>
                    </div>
                </div>
                <Tooltip title={franchise.radiusEnabled ? "Disable delivery radius" : "Enable delivery radius"}>
                    <Switch
                        size="small"
                        checked={!!franchise.radiusEnabled}
                        loading={!!togglingRadius[franchise.id]}
                        onChange={(checked) => onToggleRadius(franchise, checked)}
                    />
                </Tooltip>
            </div>

            {/* LOCATION ID & PINCODE DISPLAY */}
            <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                {franchise.location_id ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <ShopOutlined style={{ color: '#7c3aed', fontSize: 13 }} />
                        <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Location ID:</span>
                        <Tag color="purple" style={{ fontSize: 12, fontWeight: 600, margin: 0, padding: '2px 8px', borderRadius: 6 }}>
                            {franchise.location_id}
                        </Tag>
                    </div>
                ) : null}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <EnvironmentOutlined style={{ color: '#2563eb', fontSize: 13 }} />
                    <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Pincode:</span>
                    {singlePincode ? (
                        <Tag color="geekblue" style={{ fontSize: 12, fontWeight: 600, margin: 0, padding: '2px 8px', borderRadius: 6 }}>
                            {singlePincode}
                        </Tag>
                    ) : (
                        <Text type="secondary" style={{ fontSize: 12 }}>None</Text>
                    )}
                </div>
            </div>

            {/* Stats Grid */}
            <Row gutter={[8, 8]} style={{ marginBottom: 14 }}>
                <Col span={12}>
                    <div style={{ background: '#f0f9ff', borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
                        <div style={{ fontSize: 18, fontWeight: 700, color: '#0369a1' }}>{franchise.totalOrders || 0}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>Total Orders</div>
                    </div>
                </Col>
                <Col span={12}>
                    <div style={{ background: '#fefce8', borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
                        <div style={{ fontSize: 18, fontWeight: 700, color: '#a16207' }}>{franchise.pending || 0}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>Pending</div>
                    </div>
                </Col>
                <Col span={12}>
                    <div style={{ background: '#eff6ff', borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
                        <div style={{ fontSize: 18, fontWeight: 700, color: '#1d4ed8' }}>{franchise.outForDelivery || 0}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>Out for Delivery</div>
                    </div>
                </Col>
                <Col span={12}>
                    <div style={{ background: '#f0fdf4', borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
                        <div style={{ fontSize: 18, fontWeight: 700, color: '#15803d' }}>{franchise.delivered || 0}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>Delivered</div>
                    </div>
                </Col>
            </Row>

            {franchise.lastOrderAt && (
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 12 }}>
                    Last order: {new Date(franchise.lastOrderAt).toLocaleDateString()}
                </div>
            )}

            {/* Actions */}
            <div style={{ marginTop: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button
                    type="primary"
                    icon={<EyeOutlined />}
                    size="small"
                    onClick={() => onView(franchise)}
                    style={{ borderRadius: 6, flex: 1 }}
                >
                    View Orders
                </Button>
                <Tooltip title="Edit Franchise Details">
                    <Button
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => onEdit(franchise)}
                        style={{ borderRadius: 6 }}
                    />
                </Tooltip>
                <Popconfirm
                    title="Delete this franchise?"
                    description="This will remove the franchise and its assigned pincode."
                    onConfirm={() => onDelete(franchise.id)}
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                >
                    <Tooltip title="Delete">
                        <Button danger icon={<DeleteOutlined />} size="small" style={{ borderRadius: 6 }} />
                    </Tooltip>
                </Popconfirm>
            </div>
        </Card>
    );
}

// ─── Main Franchise Overview Page ─────────────────────────────────────────
export default function FranchiseOverview() {
    const screens = useBreakpoint();
    const navigate = useNavigate();
    const userDetail = useSelector(s => s.UserData.userData);
    const isStoreOwner = userDetail?.user?.role === 'store_owner' || userDetail?.role === 'user';
    const isSuperAdmin = userDetail?.role === 'superAdmin';

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [togglingRadius, setTogglingRadius] = useState({});

    const fetchOverview = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await franchiseService.overview();
            if (res?.success) {
                setData(res.data || []);
            } else {
                setError(res?.message || 'Failed to load franchises');
            }
        } catch (e) {
            setError(e?.message || 'Network error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchOverview();
    }, [fetchOverview]);

    // Navigate to dedicated Add page
    const openCreate = () => {
        navigate(routes.FRANCHISE_CREATE);
    };

    // Navigate to dedicated Edit page
    const openEdit = (franchise) => {
        navigate(`/franchise/edit/${franchise.id}`);
    };

    const handleToggleRadius = async (franchise, checked) => {
        setTogglingRadius(prev => ({ ...prev, [franchise.id]: true }));
        try {
            const res = await franchiseService.toggleRadius(franchise.id, {
                radiusEnabled: checked,
                deliveryRadiusKm: franchise.deliveryRadiusKm || 10,
            });
            if (res?.success) {
                message.success(`Delivery radius ${checked ? 'enabled' : 'disabled'} for ${franchise.name}`);
                fetchOverview();
            } else {
                errorMsg(res?.message || 'Failed to toggle radius');
            }
        } catch (e) {
            errorMsg(e?.message || 'Failed to toggle radius');
        } finally {
            setTogglingRadius(prev => ({ ...prev, [franchise.id]: false }));
        }
    };

    const handleDelete = async (id) => {
        try {
            const res = await franchiseService.delete(id);
            if (res?.success) {
                successMsg('Franchise deleted');
                fetchOverview();
            } else {
                errorMsg(res?.message || 'Failed to delete');
            }
        } catch (e) {
            errorMsg(e?.message || 'Error');
        }
    };

    const handleView = (franchise) => {
        navigate(`/franchise/${franchise.id}/orders`);
    };

    // Filter by name, email, city, address, location_id, or single pincode
    const filtered = data.filter(f => {
        if (!searchText) return true;
        const q = searchText.toLowerCase().trim();
        return (
            f.name?.toLowerCase().includes(q) ||
            f.email?.toLowerCase().includes(q) ||
            f.city?.toLowerCase().includes(q) ||
            f.address?.toLowerCase().includes(q) ||
            String(f.location_id || '').toLowerCase().includes(q) ||
            (f.pincodes || []).some(p => String(p).toLowerCase().includes(q)) ||
            String(f.pincode || '').toLowerCase().includes(q)
        );
    });

    const getColSpan = () => {
        if (screens.xxl) return 6;
        if (screens.xl) return 6;
        if (screens.lg) return 8;
        if (screens.md) return 12;
        return 24;
    };

    if (!isStoreOwner && !isSuperAdmin) {
        return (
            <MainLayout>
                <Alert message="You don't have access to Franchise management." type="error" showIcon />
            </MainLayout>
        );
    }

    const totals = data.reduce((acc, f) => ({
        total: acc.total + (f.totalOrders || 0),
        pending: acc.pending + (f.pending || 0),
        outForDelivery: acc.outForDelivery + (f.outForDelivery || 0),
        delivered: acc.delivered + (f.delivered || 0),
    }), { total: 0, pending: 0, outForDelivery: 0, delivered: 0 });

    return (
        <MainLayout>
            <div style={{ width: '100%' }}>
                {/* Page Header */}
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    flexWrap: 'wrap', gap: 16, marginBottom: 20,
                }}>
                    <div>
                        <h2 style={{
                            margin: 0, fontSize: 22, fontWeight: 700, color: '#0f172a',
                            letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: 8,
                        }}>
                            <ShopOutlined style={{ color: '#2563eb' }} /> Franchise Management
                        </h2>
                        <div style={{ fontSize: 13, color: '#64748b', marginTop: 3 }}>
                            Manage franchise hubs, delivery coverage radius, and single pincode-wise order operations
                        </div>
                    </div>
                    <Space size={10} wrap>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={fetchOverview}
                            loading={loading}
                            size="large"
                            style={{ borderRadius: 8, fontWeight: 500 }}
                        >
                            Refresh
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={openCreate}
                            size="large"
                            style={{
                                borderRadius: 8, fontWeight: 600,
                                boxShadow: '0 2px 6px rgba(37, 99, 235, 0.25)',
                            }}
                        >
                            Add Franchise
                        </Button>
                    </Space>
                </div>

                {/* Summary Stats */}
                {data.length > 0 && (
                    <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                        {[
                            { title: 'Total Franchises', value: data.length, color: '#2563eb', bg: '#eff6ff' },
                            { title: 'Total Orders', value: totals.total, color: '#0369a1', bg: '#f0f9ff' },
                            { title: 'Pending', value: totals.pending, color: '#a16207', bg: '#fefce8' },
                            { title: 'Delivered', value: totals.delivered, color: '#15803d', bg: '#f0fdf4' },
                        ].map(stat => (
                            <Col xs={12} sm={6} key={stat.title}>
                                <Card style={{ borderRadius: 10, border: '1px solid #e2e8f0', background: stat.bg }}>
                                    <Statistic
                                        title={<span style={{ fontSize: 12, color: '#64748b' }}>{stat.title}</span>}
                                        value={stat.value}
                                        valueStyle={{ fontSize: 24, fontWeight: 700, color: stat.color }}
                                    />
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}

                {/* Search */}
                <div style={{ marginBottom: 16, maxWidth: 400 }}>
                    <Input
                        prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                        placeholder="Search by name, city, location ID, pincode..."
                        allowClear
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        size="middle"
                        style={{ borderRadius: 8 }}
                    />
                </div>

                {/* Error */}
                {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} closable onClose={() => setError(null)} />}

                {/* Franchise Cards Grid */}
                {!loading && filtered.length === 0 ? (
                    <Card style={{ borderRadius: 12, textAlign: 'center', padding: 40 }}>
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                data.length === 0
                                    ? <span>No franchises yet.<br /><Button type="link" onClick={openCreate}>Add your first franchise</Button></span>
                                    : 'No franchises match your search.'
                            }
                        />
                    </Card>
                ) : (
                    <Row gutter={[16, 16]}>
                        {(loading
                            ? Array.from({ length: 4 }).map((_, i) => ({ id: `sk-${i}` }))
                            : filtered
                        ).map(franchise => (
                            <Col key={franchise.id} span={getColSpan()}>
                                {loading ? (
                                    <Card loading style={{ borderRadius: 12, minHeight: 280 }} />
                                ) : (
                                    <FranchiseStatCard
                                        franchise={franchise}
                                        onView={handleView}
                                        onEdit={openEdit}
                                        onDelete={handleDelete}
                                        onToggleRadius={handleToggleRadius}
                                        togglingRadius={togglingRadius}
                                    />
                                )}
                            </Col>
                        ))}
                    </Row>
                )}
            </div>
        </MainLayout>
    );
}
